import { useState, type ErrorInfo } from 'react'
import type { ErrorFallbackProps } from '@parkify/ui/error-boundary'
import { isStaleChunkRecovery, reloadForStaleChunk } from '../chunkRecovery'

// Two fallbacks for the two boundaries ops mounts (both are @parkify/ui's
// ErrorBoundary):
//
//   • ROOT (main.tsx) — last line of defence around the whole console. Owns
//     stale-chunk recovery: a chunk error reloads once and shows a calm
//     spinner; anything else shows the full-page crash card.
//   • SCREEN (App.tsx, around <Routes>) — keeps the sidebar and header on
//     screen when one screen throws, and clears itself on navigation via
//     resetKey. It never handles a stale chunk itself: it rethrows so the root
//     boundary reloads, exactly as it did before this boundary existed.
//     It does not print error.message (operators get Try again and Reload);
//     the boundary already logs the error to the console.

// ─── Root ───────────────────────────────────────────────────────────────────

// componentDidCatch half of the root boundary. Runs in the commit phase,
// after AppErrorFallback's first render has captured `recovering` and before
// any effect, so isStaleChunkRecovery() still sees the pre-reload throttle.
export function onAppError(error: Error, info: ErrorInfo) {
  if (isStaleChunkRecovery(error)) {
    // Throttled — a no-op if the vite:preloadError listener already reloaded.
    reloadForStaleChunk()
    return
  }
  // Worker /healthz aggregation reads CF GraphQL, not console logs from
  // browsers, so this stays as a plain console.error for now. If we add
  // a client-side error reporter (e.g. Sentry browser SDK) later, fire it here.
  console.error('[ErrorBoundary]', error, info.componentStack)
}

export function AppErrorFallback({ error }: ErrorFallbackProps) {
  // Decided once, on the very first render after the throw (before
  // onAppError stamps the reload throttle), then held: re-evaluating after the
  // stamp would flip a recovering spinner into the crash card mid-reload.
  const [recovering] = useState(() => isStaleChunkRecovery(error))
  // Stale-chunk reload is in flight — show a calm "updating" spinner rather
  // than the alarming crash card; the page is about to navigate away.
  if (recovering) return <ReloadingScreen />
  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at top, rgba(127,63,152,0.4), #0a0a0a 60%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        width: '100%', maxWidth: 560, background: 'rgba(20,20,24,0.94)',
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18,
        padding: '36px 36px 32px', color: 'white',
        boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
      }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '3px', color: '#7F3F98', marginBottom: 12 }}>
          CONSOLE · UNEXPECTED ERROR
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 12px' }}>
          Something broke on this screen
        </h1>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.65, margin: '0 0 20px' }}>
          We've logged it. Reloading should put the console back in a
          working state — if it keeps happening on the same screen, send
          the message below to the team.
        </p>
        <div style={{
          background: '#0a0a0a',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 8, padding: '12px 14px',
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          fontSize: 12, color: 'rgba(255,255,255,0.85)',
          wordBreak: 'break-word', whiteSpace: 'pre-wrap',
          maxHeight: 200, overflowY: 'auto',
        }}>
          {error.message}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 'var(--radius-btn)',
              background: '#7F3F98', color: 'white', border: 'none',
              fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Reload
          </button>
          <button
            onClick={() => { window.location.href = '/' }}
            style={{
              padding: '12px 16px', borderRadius: 'var(--radius-btn)',
              background: 'transparent', color: 'white',
              border: '1px solid rgba(255,255,255,0.2)',
              fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Go to home
          </button>
        </div>
      </div>
    </div>
  )
}

// Shown for the brief moment between catching a stale-chunk error and the
// recovery reload committing. Light background matches the app's normal theme
// so there's no dark flash, and the copy reassures rather than alarms — the
// operator just sees the console quietly pulling the latest build.
function ReloadingScreen() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 16,
      background: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        border: '3px solid rgba(127,63,152,0.18)', borderTopColor: '#7F3F98',
        animation: 'parkify-reload-spin 0.7s linear infinite',
      }} />
      <div style={{
        fontSize: 12, fontWeight: 700, letterSpacing: '1.5px',
        textTransform: 'uppercase', color: '#8a8595',
      }}>
        Updating to the latest version
      </div>
      <style>{`@keyframes parkify-reload-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ─── Screen ─────────────────────────────────────────────────────────────────

export function ScreenErrorFallback({ error, reset }: ErrorFallbackProps) {
  // A lazy route's import() rejects inside this boundary. Only the root knows
  // how to recover from that (one throttled reload + spinner), so hand it up.
  // Throwing from a boundary's fallback propagates to the next boundary out.
  if (isStaleChunkRecovery(error)) throw error
  return (
    <div role="alert" data-testid="screen-error" style={{
      maxWidth: 560, margin: '48px auto', padding: '28px 28px 24px',
      background: 'var(--color-white)', border: '1px solid var(--color-gray-200)',
      borderRadius: 14, boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '3px', color: '#7F3F98', marginBottom: 10 }}>
        SCREEN · UNEXPECTED ERROR
      </div>
      <h2 style={{ fontSize: 19, fontWeight: 800, margin: '0 0 10px', color: 'var(--color-black)' }}>
        This screen hit an error
      </h2>
      <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0, color: 'var(--color-gray-600)' }}>
        The rest of the console still works: pick another section from the
        menu, or try this one again. If it keeps happening, reload the page.
      </p>
      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button
          onClick={reset}
          style={{
            padding: '10px 16px', borderRadius: 'var(--radius-btn)',
            background: '#7F3F98', color: 'white', border: 'none',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Try again
        </button>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 16px', borderRadius: 'var(--radius-btn)',
            background: 'transparent', color: 'var(--color-gray-700)',
            border: '1px solid var(--color-gray-300)',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    </div>
  )
}
